import { ActionReducerMapBuilder, AsyncThunk, PayloadAction, SerializedError } from '@reduxjs/toolkit';

import { FilterState } from './filterSliceUtils';

/**
 * State shape for slices with entities and a loaded flag
 */
export interface LoadedEntityState<T> extends FilterState {
    entities: T[];
    loaded: boolean;
}

/**
 * State shape for slices with entities, processing, and error handling
 */
export interface ProcessingEntityState<T> extends FilterState {
    entities: T[];
    processing: boolean;
    error?: SerializedError;
}

/**
 * Creates initial state for entity slices with a loaded flag
 */
export const createLoadedEntityInitialState = <T>(): LoadedEntityState<T> => ({
    entities: [],
    loaded: false,
    filters: {
        keywords: [],
    },
});

/**
 * Creates initial state for entity slices with processing and error
 */
export const createProcessingEntityInitialState = <T>(): ProcessingEntityState<T> => ({
    entities: [],
    processing: false,
    filters: {
        keywords: [],
    },
});

/*
 * Note on type safety: These utilities use minimal `any` types due to TypeScript limitations:
 * 1. ActionReducerMapBuilder is invariant - can't accept different state shapes
 * 2. AsyncThunk arg type is contravariant - can't accept thunks with specific arg types
 * The `any` usage is intentional and isolated to allow flexibility across different slices.
 */

/**
 * Creates extra reducers for async thunks with a simple loaded flag pattern.
 * Pattern: pending -> clear entities, set loaded false
 *          fulfilled -> set entities, set loaded true
 *          rejected -> clear entities, set loaded false
 *
 * @param builder - Redux toolkit builder from extraReducers callback
 * @param thunk - AsyncThunk that returns an array of entities
 * @param entityKey - The key in the state where entities are stored (default: 'entities')
 */
export const createLoadedExtraReducers = <TEntity>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Builder state shape varies; invariance prevents proper typing
    builder: ActionReducerMapBuilder<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Thunk arg type varies; contravariance prevents proper typing
    thunk: AsyncThunk<TEntity[], any, any>,
    entityKey: string = 'entities'
) => {
    type State = { loaded: boolean; [key: string]: unknown };
    builder.addCase(thunk.pending, (state: State) => {
        state.loaded = false;
        state[entityKey] = [];
    });
    builder.addCase(thunk.fulfilled, (state: State, action: PayloadAction<TEntity[]>) => {
        state.loaded = true;
        state[entityKey] = action.payload;
    });
    builder.addCase(thunk.rejected, (state: State) => {
        state.loaded = false;
        state[entityKey] = [];
    });
};

/**
 * Creates extra reducers for async thunks with processing and error handling pattern.
 * Pattern: pending -> set processing true, clear entities
 *          fulfilled -> set processing false, clear error, set entities
 *          rejected -> set processing false, set error (ignore AbortError), clear entities
 *
 * @param builder - Redux toolkit builder from extraReducers callback
 * @param thunk - AsyncThunk that returns an array of entities
 */
export const createProcessingExtraReducers = <TEntity>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Builder state shape varies; invariance prevents proper typing
    builder: ActionReducerMapBuilder<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Thunk arg type varies; contravariance prevents proper typing
    thunk: AsyncThunk<TEntity[], any, any>
) => {
    type State = ProcessingEntityState<TEntity>;
    builder.addCase(thunk.pending, (state: State) => {
        state.processing = true;
        state.entities = [];
    });
    builder.addCase(thunk.fulfilled, (state: State, action: PayloadAction<TEntity[]>) => {
        state.processing = false;
        state.error = undefined;
        state.entities = action.payload;
    });
    builder.addCase(thunk.rejected, (state: State, action) => {
        state.processing = false;
        const error = action.error as SerializedError | undefined;
        if (error?.name !== 'AbortError') {
            state.error = error;
        }
        state.entities = [];
    });
};

/**
 * Reducer to clear entities
 */
export const clearEntitiesReducer = <T>(state: { entities: T[] }) => {
    state.entities = [];
};
